import React, { useState } from 'react';
import { CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const JobComparisonQuestion = ({
  questions,
  results,
  currentAnswers,
  submittedFirstTime,
  showExplanation,
  canRetry,
  onAnswerChange,
  onToggleExplanation,
  showWordBank = true
}) => {

  /* 🔥 FIX 1 — chỉ cho phép mở 1 explanation */
  const [openExplanation, setOpenExplanation] = useState({});

  const toggleOneExplanation = (id) => {
    setOpenExplanation(prev => {
      if (prev[id]) return {};
      return { [id]: true };
    });
  };

  if (!questions || questions.length === 0) return null;

  const extractCriteria = (text) => {
    if (!text) return "";
    return text.split("(")[0].trim().replace(/:$/, "");
  };

  const instruction = questions?.[0]?.instruction || "";
  const wordBank = questions?.[0]?.word_bank || [];

  const wordBankOptions = wordBank.map((word, index) => ({
    value: String.fromCharCode(65 + index),
    label: word
  }));

  const isOptionUsed = (optionValue, currentQuestionId) => {
    if (!currentAnswers) return false;

    const ids = questions.map(q => q.id);
    return ids.some(qid => {
      if (qid === currentQuestionId) return false;
      return currentAnswers[`q${qid}`] === optionValue;
    });
  };

  const getOptionLabel = (value) => {
    const opt = wordBankOptions.find(o => o.value === value);
    return opt ? opt.label : "";
  };

  /* 🔧 Hàm remove tiền tố “A:”, “B:”, “C:” khỏi explanation */
  const stripPrefix = (text) => {
    if (!text) return "";
    return text.replace(/^[A-Z]\s*:\s*/, "").trim();
  };

  /* 🎯 FIX QUAN TRỌNG — giống MatchingQuestion */
  const handleAnswerChange = (questionId, value) => {
    if (!submittedFirstTime || canRetry) {
      onAnswerChange(questionId, value);
    }
  };

  return (
    <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-white">

      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
            1-7
          </span>
          <span className="text-sm font-medium text-gray-700">Job Comparison Table</span>
        </div>

        {submittedFirstTime && (
          <button
            onClick={onToggleExplanation}
            className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1"
          >
            {showExplanation ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>{showExplanation ? 'Hide' : 'Show'} Explanation</span>
          </button>
        )}
      </div>

      {instruction && (
        <div className="mb-4 p-3 bg-gray-50 border rounded text-sm text-gray-700">
          {instruction}
        </div>
      )}

      {showWordBank && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">
            Word Bank ({wordBankOptions.length})
          </h4>

          <div className="grid grid-cols-2 gap-2 text-sm">
            {wordBankOptions.map((option) => {
              const isUsed = questions.some(
                q => currentAnswers?.[`q${q.id}`] === option.value
              );

              return (
                <div key={option.value} className={`flex items-center ${isUsed ? 'opacity-60' : ''}`}>
                  <span className="font-medium text-blue-700 mr-2">{option.value}.</span>
                  <span className={`${isUsed ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                    {option.label}
                  </span>
                  {isUsed && <span className="ml-2 text-xs text-red-500 font-medium">✓ Used</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-800 w-1/4">
                Criteria
              </th>
              <th className="border border-gray-300 px-4 py-3 text-center font-bold text-gray-800 w-3/8">
                Job Advertisement 1
              </th>
              <th className="border border-gray-300 px-4 py-3 text-center font-bold text-gray-800 w-3/8">
                Job Advertisement 2
              </th>
            </tr>
          </thead>

          <tbody>
            {questions.map((q, idx) => {
              const criteria = extractCriteria(q.question_text);

              let currentAnswer = currentAnswers?.[`q${q.id}`] ?? "";

              const correctAnswer = q.correct_answer;
              const submittedSelected = results?.find(r => r.id === q.id)?.selected || null;

              const isCorrect = submittedFirstTime && submittedSelected === correctAnswer;
              const isIncorrect = submittedFirstTime && submittedSelected && submittedSelected !== correctAnswer;
              const showThisExplanation = openExplanation[q.id] === true;

              const job2Text =
                q.question_text.includes(":")
                  ? q.question_text.split(":")[1].trim()
                  : "";

              return (
                <tr key={q.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>

                  <td className="border px-4 py-3 font-semibold text-gray-800">
                    {criteria}
                  </td>

                  <td className="border px-4 py-3">

                    <p className="text-sm text-gray-700 mb-2">{q.question_text}</p>

                    {submittedFirstTime && (
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-center text-sm">
                          {isCorrect ? (
                            <CheckCircle className="w-4 h-4 text-green-600 mr-1" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600 mr-1" />
                          )}
                          {submittedSelected && (
                            <span className="text-xs text-gray-500">
                              Your previous answer: {submittedSelected}. {getOptionLabel(submittedSelected)}
                            </span>
                          )}
                        </div>

                        <button
                          onClick={() => toggleOneExplanation(q.id)}
                          className="text-blue-600 hover:underline text-xs flex items-center ml-2"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Explanation
                        </button>
                      </div>
                    )}

                    <div className={`${isIncorrect ? "border border-yellow-500 bg-yellow-50 p-2 rounded-md" : ""}`}>

                      {isIncorrect && (
                        <p className="text-xs text-yellow-700 mb-1">Try again for 0.5 points:</p>
                      )}

                      <select
                        value={currentAnswer}
                        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                        disabled={submittedFirstTime && isCorrect}
                        className="w-full p-2 border border-gray-300 rounded text-sm"
                      >
                        <option value="">Select...</option>

                        {wordBankOptions.map((opt) => {
                          const disabled =
                            isOptionUsed(opt.value, q.id) &&
                            currentAnswer !== opt.value;

                          return (
                            <option
                              key={opt.value}
                              value={opt.value}
                              disabled={disabled}
                              className={disabled ? "text-gray-400 bg-gray-100" : ""}
                            >
                              {opt.value}. {opt.label}
                            </option>
                          );
                        })}
                      </select>

                      {submittedFirstTime && showThisExplanation && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                          <strong>Explanation:</strong>

                          <p className="text-gray-700 mt-1">

                            {isCorrect
                              ? (
                                q.answer_explanation
                                  ? stripPrefix(q.answer_explanation)
                                  : stripPrefix(q.feedback?.correct) || "Good job!"
                              )
                              : (
                                q.feedback?.incorrect ||
                                "Please review the explanation."
                              )
                            }

                          </p>
                        </div>
                      )}

                    </div>
                  </td>

                  <td className="border px-4 py-3 text-sm text-gray-700">
                    {job2Text}
                  </td>

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default JobComparisonQuestion;
